import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, ChevronDownOutline, Menu, MenuButton, MenuItem, MenuList, rem, Text } from '@bonusly/design-system';

import { Award, GetIncentivesSummaryParams } from '../hooks/use-incentives';

interface Props {
	awards: Award[];
	params: GetIncentivesSummaryParams;
	setParams: (params: GetIncentivesSummaryParams) => void;
}

export const NameFilter: FC<Props> = ({ awards, params, setParams }) => {
	const { t } = useTranslation('admin', {
		keyPrefix: 'incentives.summary.filters',
	});
	const { track } = useAnalytics();

	const selectedAwardName = params.award_ids.length === 0 ? t('all_incentives') : awards.find((award) => award.id === params.award_ids[0])?.name;

	const handleClick = (id: string) => {
		const incentiveName = id === 'all' ? 'All Incentives' : awards.find((award) => award.id === id)?.name;
		track(AnalyticsEvent.AdminIncentives.SummaryFilterIncentive, {
			incentiveName,
		});

		setParams({
			...params,
			page_number: 1,
			award_ids: id === 'all' ? [] : [id],
		});
	};

	return (
		<Menu>
			<MenuButton
				as={Button}
				bgColor="bg.surface"
				borderWidth={rem(1)}
				borderStyle="solid"
				borderColor="border.input"
				borderRadius="md"
				color="text.default"
				data-testid="name-filter-button"
				fontSize={100}
				fontWeight="normal"
				rightIcon={<ChevronDownOutline size="sm" />}
				textAlign="left"
				w={rem(300)}
				_active={{
					borderColor: 'border.input',
					bgColor: 'bg.surface',
					color: 'text.default',
				}}
				_focusVisible={{
					borderColor: 'border.focus',
					borderWidth: rem(1.5),
					boxShadow: 'none',
				}}
				_hover={{
					borderColor: 'border.input',
					cursor: 'pointer',
				}}
			>
				<Text overflow="hidden" textOverflow="ellipsis">
					{selectedAwardName}
				</Text>
			</MenuButton>
			<MenuList minW={rem(300)} maxH={rem(530)} overflow="scroll">
				<MenuItem bgColor={selectedAwardName === t('all_incentives') ? 'bg.soft' : 'bg.surface'} onClick={() => handleClick('all')}>
					{t('all_incentives')}
				</MenuItem>
				{awards.map((award) => (
					<MenuItem key={award.id} bgColor={selectedAwardName === award.name ? 'bg.soft' : 'bg.surface'} onClick={() => handleClick(award.id)}>
						{award.name}
					</MenuItem>
				))}
			</MenuList>
		</Menu>
	);
};
